import React, { ReactNode } from 'react';
import PageWrapper from '../../components/layouts/PageWrapper/PageWrapper';
import Container from '../../components/layouts/Container/Container';
import Button from '../../components/ui/Button';
import { HeroImg } from '../../assets/images';
import { TIcons } from '../../types/icons.type';
import Icon from '../../components/icon/Icon';
import { useTranslation } from 'react-i18next';
import useHash from '../../hooks/useHash';
import { customerViewPages } from '../../config/pages.config';
import { Link } from 'react-router-dom';

const IconContentPartial = ({
	icon,
	title,
	children,
}: {
	icon: TIcons;
	title: string;
	children: ReactNode;
}) => {
	return (
		<div>
			<div className='flex flex-wrap gap-4'>
				<div className='flex items-center justify-center'>
					<Icon icon={icon} size='text-5xl' color='blue' />
				</div>
				<div className='flex basis-full text-2xl font-extrabold'>{title}</div>
				<div className='flex basis-full'>
					<div className='text-zinc-600 dark:text-zinc-400'>{children}</div>
				</div>
			</div>
		</div>
	);
};

const ConsultingPage = () => {
	const { t } = useTranslation();
	useHash();
	return (
		<PageWrapper isProtectedRoute={false} title={t('Consultants')}>
			<Container breakpoint={null} className='w-full'>
				<section
					id='top'
					className='-mx-4 -mt-[var(--header-height)] bg-indigo-500/5 pt-[var(--header-height)] lg:pt-[calc(var(--header-height)+6rem)]'>
					<div className='py-10 lg:m-0 xl:py-8 2xl:py-16'>
						<div className='container mx-auto px-4'>
							<div className='grid grid-cols-12 gap-14 lg:gap-32'>
								<div className='col-span-12 flex w-full flex-col flex-wrap gap-4 lg:col-span-4'>
									<h1 className='sticky top-[calc(var(--header-height)+1rem)] mb-4 max-w-2xl text-4xl font-extrabold !leading-snug text-zinc-700 dark:text-white md:text-5xl xl:text-6xl'>
										{t('Consultants')}
									</h1>
								</div>
								<div className='col-span-12 flex w-full flex-col flex-wrap gap-4 lg:col-span-8'>
									<p className='text-justify text-2xl font-extrabold !leading-relaxed text-zinc-700 dark:text-white'>
										{t('Simplify Your Business Workflows with Our Platform')}
									</p>
									<p className='text-justify font-light !leading-relaxed text-zinc-600 dark:text-zinc-400 md:text-lg lg:text-xl'>
										{t(
											'Our platform is designed to make your consulting services more efficient, organized, and transparent.',
										)}{' '}
										{t(
											"Whether you're a single consultant or a team, managing multiple clients or handling complex projects, our tools give you everything you need to stay in control and improve collaboration with your customers.",
										)}
									</p>
									<p className='text-justify text-2xl font-extrabold !leading-relaxed text-zinc-700 dark:text-white'>
										{t('For Consultants')}
									</p>
									<ul className='list-inside list-disc text-justify font-light !leading-relaxed text-zinc-600 dark:text-zinc-400 md:text-lg lg:text-xl'>
										<li>
											<span className='font-extrabold'>
												{t('Manage Client Profiles:')}{' '}
											</span>
											{t(
												'Keep customer information up to date in one centralized system.',
											)}
										</li>
										<li>
											<span className='font-extrabold'>
												{t('Track & Organize Invoices:')}{' '}
											</span>
											{t(
												'Ensure accurate and timely billing with streamlined invoicing tools.',
											)}
										</li>
										<li>
											<span className='font-extrabold'>
												{t('Oversee Projects & Tasks:')}{' '}
											</span>
											{t(
												'Keep projects on schedule by monitoring deadlines and milestones.',
											)}
										</li>
										<li>
											<span className='font-extrabold'>
												{t('Monitor Timeslips:')}{' '}
											</span>
											{t(
												'Log time directly against client tasks for clear, transparent billing.',
											)}
										</li>
										<li>
											<span className='font-extrabold'>
												{t('Automate Tasks & Billing:')}{' '}
											</span>
											{t(
												'Save valuable time by automating routine processes, reducing admin work.',
											)}
										</li>
									</ul>
									<p className='text-justify font-light !leading-relaxed text-zinc-600 dark:text-zinc-400 md:text-lg lg:text-xl'>
										{t(
											'These features simplify your daily operations so you can focus on delivering value to your clients.',
										)}
									</p>
									<p className='text-justify text-2xl font-extrabold !leading-relaxed text-zinc-700 dark:text-white'>
										{t('For Your Customers')}
									</p>
									<ul className='list-inside list-disc text-justify font-light !leading-relaxed text-zinc-600 dark:text-zinc-400 md:text-lg lg:text-xl'>
										<li>
											<span className='font-extrabold'>
												{t('Transparent Access:')}{' '}
											</span>
											{t(
												'Customers can view and manage their information, invoices, projects, and tasks.',
											)}
										</li>
										<li>
											<span className='font-extrabold'>
												{t('Timeslip Visibility:')}{' '}
											</span>
											{t(
												'Clients can see detailed records of registered hours, promoting trust and clarity.',
											)}
										</li>
									</ul>
									<p className='text-justify font-light !leading-relaxed text-zinc-600 dark:text-zinc-400 md:text-lg lg:text-xl'>
										{t(
											'By providing this level of access and insight, you build stronger relationships and better communication with your clients.',
										)}
									</p>
									<p className='text-justify text-2xl font-extrabold !leading-relaxed text-zinc-700 dark:text-white'>
										{t('Why Choose Our Platform?')}
									</p>
									<p className='text-justify font-light !leading-relaxed text-zinc-600 dark:text-zinc-400 md:text-lg lg:text-xl'>
										{t(
											'Our solution eliminates the hassle of manual tracking and disconnected systems.',
										)}{' '}
										{t(
											'It centralizes your consulting activities, empowers clients to stay informed, and boosts productivity.',
										)}
									</p>
									<p className='text-justify text-2xl font-extrabold !leading-relaxed text-zinc-700 dark:text-white'>
										{t(
											'Take the next step with our platform and elevate your consulting business today!',
										)}
									</p>
									<div className='flex shrink-0'>
										<Link
											to={{
												pathname: customerViewPages.contactUsPage.to,
												search: '?subject=Demo&description=Please contact me to schedule a demo',
											}}>
											<Button variant='solid' size='xl'>
												{t('Start your project')}
											</Button>
										</Link>
									</div>
								</div>
							</div>
						</div>
					</div>
				</section>
				<section className='-mx-4 bg-indigo-500/5'>
					<div className='overflow-hidden py-10 lg:m-0 xl:py-8 2xl:py-16'>
						<div className='container mx-auto px-4'>
							<div className='grid grid-cols-1 items-center gap-14 border-t border-zinc-500/50 py-20 lg:grid-cols-12 lg:gap-32'>
								<div className='flex w-full flex-wrap gap-8 lg:col-span-6'>
									<div className='grid grid-cols-1 items-center gap-4'>
										<div className='flex w-full flex-wrap'>
											<div className='mb-16 flex justify-center text-3xl font-extrabold dark:text-white md:text-4xl xl:text-5xl'>
												{t('Features')}
											</div>
										</div>
										<div className='flex w-full flex-wrap'>
											<div className='grid grid-cols-1 gap-16 sm:grid-cols-2 md:grid-cols-2'>
												<IconContentPartial
													icon='HeroRocketLaunch'
													title={t('Projekthantering')}>
													{t(
														'Koppla försäljning och arbetstid till projekt',
													)}
												</IconContentPartial>
												<IconContentPartial
													icon='HeroHandThumbUp'
													title={t('Ärendehantering')}>
													{t(
														'Skapa ärenden, koppla till projekt, kategorisera ärenden och rapportera arbetstid',
													)}
												</IconContentPartial>
												<IconContentPartial
													icon='HeroUsers'
													title={t('Arbetstid')}>
													{t(
														'Kategorisera och rapportera betald och icke betald arbetstid. Kunder kan logga in och se och godkänna upparbetad arbetstid innan ni fakturerar',
													)}
												</IconContentPartial>
												<IconContentPartial
													icon='HeroClock'
													title={t('Business intelligence')}>
													{t(
														'Lär er mer om er verksamhet genom att utforska data och övervaka nyckeltal',
													)}
												</IconContentPartial>
												<IconContentPartial
													icon='HeroClock'
													title={t('Händelser')}>
													{t(
														'För varje händelse i kundportalen skapas en logg som ni sedan kan ta del av',
													)}
												</IconContentPartial>
											</div>
										</div>
									</div>
								</div>
								<div className='block w-full lg:col-span-6'>
									<div className='w-full max-w-[96rem] rounded-3xl bg-gradient-to-r from-zinc-500/50 to-purple-950/25 p-6 sm:w-auto lg:w-[55vw]'>
										<img
											src={HeroImg}
											alt='Dashboard image'
											className='w-full rounded-xl object-cover lg:h-auto'
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				</section>
			</Container>
		</PageWrapper>
	);
};

export default ConsultingPage;
